import { Component, NgModule, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { EvaluationFormComponent } from './evaluation-form/evaluation-form.component';
import { MessageService } from 'primeng/api';
import { EvaluationFilterService } from '../../../core/services/evaluation-filter.service';
import { NgFor, NgIf } from '@angular/common';
import { TabViewModule } from 'primeng/tabview';
import { RemunerationGridComponent } from './remuneration-grid/remuneration-grid.component';
import { PermissionService } from '../../../core/services/permission.service';
import { PracticeScopeService } from '../../../core/services/practice-scope.service';
import { SharedEvaluationService } from '../../../core/services/shared-evaluation.service';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { EvaluationSalaryRangesComponent } from './evaluation-salary-ranges/evaluation-salary-ranges.component';
import { practiceOfCategoryKey } from '../../../core/utils/category-tree';

@Component({
  selector: 'em-evaluation',
  standalone: true,
  imports: [
    ButtonModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TableModule,
    TabViewModule,
    TranslatePipe,
    NgFor,
    NgIf,
    RemunerationGridComponent,
    EmPermissionDirective,
    EvaluationSalaryRangesComponent,
  ],
  templateUrl: './evaluation.component.html',
  styleUrl: './evaluation.component.scss'
})
export class EvaluationComponent {
  dialogRef!: DynamicDialogRef;
  translations: any = {};
  filters: any[] = [];
  Permission = Permission;

  @ViewChild(EvaluationSalaryRangesComponent) salaryRanges?: EvaluationSalaryRangesComponent;
  @ViewChild(RemunerationGridComponent) remunerationGrid?: RemunerationGridComponent;

  readonly canSeeCategory: boolean;
  readonly canSeeRemuneration: boolean;

  constructor(
    private dialogService: DialogService,
    private messageService: MessageService,
    private evaluationFilterService: EvaluationFilterService,
    private translate: TranslateService,
    private sharedEvaluationService: SharedEvaluationService,
    permissionService: PermissionService,
    private practiceScope: PracticeScopeService,
  ) {
    // The Categories are also shown to the managers who are confined to their own
    // practice: they only ever see that practice's categories.
    this.canSeeCategory = permissionService.hasPermission(Permission.CONFIG_PERFORMANCE)
      || practiceScope.isPracticeScopedUser();
    this.canSeeRemuneration = permissionService.hasAnyPermission([
      Permission.MANAGER_READ,
      Permission.MANAGER_WRITE,
      Permission.DIRECTOR_READ,
      Permission.DIRECTOR_WRITE,
    ]);
  }

  ngOnInit(): void {
    this.loadTranslation();
    if (this.canSeeCategory) {
      this.loadFilters();
    }
  }
  
  private loadTranslation(): void {
    this.translate.get([
      'layout.configurations.evaluation.addFilter',
      'layout.configurations.evaluation.label',
      'layout.configurations.evaluation.addEvaluationFilter.title',
      'layout.configurations.evaluation.addEvaluationFilter.save',
      'layout.configurations.evaluation.addEvaluationFilter.successMessage.title',
      'layout.configurations.evaluation.addEvaluationFilter.successMessage.detail',
      'layout.configurations.evaluation.addEvaluationFilter.errorMessage.title',
      'layout.configurations.evaluation.addEvaluationFilter.errorMessage.error',
      'layout.configurations.evaluation.tabs.filters',
      'layout.configurations.evaluation.tabs.groups',
      'layout.configurations.evaluation.tabs.salaryRanges',
    ]).subscribe((response) => {
      this.translations = response;
    });
  }

  private loadFilters(filters?: any): void {
    if (filters) {
      this.filters = this.restrictToPractice(filters);
    } else {
      this.evaluationFilterService.getEvaluationFilter().subscribe((response) => {
        this.filters = this.restrictToPractice(JSON.parse(response?.filterJson || '[]'));
      })
    }
  }

  /**
   * A Manager / Manager People only sees the categories of their own practice, so
   * the list never shows another practice's entries. Unrestricted users keep the
   * full list.
   *
   * The practice comes from the saved entry itself: newer entries carry it, and
   * for the older ones it is decoded from the key, which holds it as well.
   */
  private restrictToPractice(filters: any[]): any[] {
    return this.practiceScope.restrict(
      filters ?? [],
      (filter) => filter?.practice ?? practiceOfCategoryKey(filter?.key),
    );
  }

  public includeForm(): void {
      const originalFilters = JSON.stringify(this.filters); // Keep a copy for comparison

    this.dialogRef = this.dialogService.open(EvaluationFormComponent, {
      header: this.translations['layout.configurations.evaluation.addFilter'],
      width: '560px',
      // No fixed height. The tree is inline and scrolls internally, so the dialog
      // sizes to its content. Matches the pool filter dialog.
      style: { 'max-width': '95vw' },
      dismissableMask: true,
      data: { scopedPractice: this.practiceScope.getScopedPractice() },
    })

    this.dialogRef.onClose.subscribe((response) => {
      if (response?.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.evaluation.addEvaluationFilter.errorMessage.title'),
          detail: this.translate.instant('layout.configurations.evaluation.addEvaluationFilter.errorMessage.errorSavingEvaluationFilter'),
        });
        return;
      }
      if (response?.saved && response?.filters) {
      const updatedFilters = JSON.stringify(response.filters);

      if (originalFilters === updatedFilters) {
        // No changes made
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage') 
        });
      } else {
        this.sharedEvaluationService.triggerReload();
        this.loadFilters(response.filters)
        this.messageService.add({
          severity: "success",
          summary: this.translations["layout.configurations.evaluation.addEvaluationFilter.successMessage.title"],
          detail: this.translations["layout.configurations.evaluation.addEvaluationFilter.successMessage.detail"]
        });
      }}
    });
  }

  /**
   * Tab panel content is projected once and never destroyed, so each child keeps
   * its own stale list. Refresh them when the active tab changes: a category added
   * in the Categories tab then becomes selectable in the Salary Range tab.
   */
  onTabChange(): void {
    this.salaryRanges?.getAll();
    this.remunerationGrid?.refreshPractices();
  }

}
